import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, Wallet, Package, RefreshCw, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientHistoryPage = () => {
  const { getAuthHeaders } = useAuth();
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/client/history?period=${period}`, { headers: getAuthHeaders() });
      setTimeline(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [period]);

  const periods = [
    { value: 'week', label: 'Semanal' },
    { value: 'month', label: 'Mensual' },
    { value: 'year', label: 'Anual' },
    { value: 'all', label: 'Todo' },
  ];

  const typeConfig = {
    wallet: { icon: Wallet, color: 'text-emerald-500', bg: 'bg-emerald-50' },
    order: { icon: Package, color: 'text-blue-500', bg: 'bg-blue-50' },
    divisa: { icon: RefreshCw, color: 'text-violet-500', bg: 'bg-violet-50' },
  };

  const getDescription = (item) => {
    if (item.type === 'wallet') return item.description || item.transaction_type;
    if (item.type === 'order') return `Orden: ${item.service_type}`;
    if (item.type === 'divisa') return `Divisa: ${item.from_currency} → ${item.to_currency}`;
    return 'Movimiento';
  };

  const getAmount = (item) => {
    if (item.type === 'wallet') return item.amount;
    if (item.type === 'order') return -(item.amount || 0);
    if (item.type === 'divisa') return -(item.send_amount || 0);
    return 0;
  };

  const getStatus = (item) => {
    if (item.type === 'wallet') return item.status;
    if (item.type === 'order') return item.status;
    if (item.type === 'divisa') return item.status;
    return '';
  };

  const statusLabel = {
    approved: 'Aprobado', pending: 'Pendiente', rejected: 'Rechazado',
    completed: 'Completado', cancelled: 'Cancelado', processing: 'Procesando', sent: 'Enviado', in_progress: 'En Proceso'
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="client-history-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Historial</h1>
        <p className="text-slate-500">Todos tus movimientos en un solo lugar</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {periods.map((p) => (
          <Button key={p.value} variant={period === p.value ? 'default' : 'outline'} size="sm"
            onClick={() => setPeriod(p.value)}
            className={period === p.value ? 'bg-emerald-700' : ''} data-testid={`period-${p.value}`}>
            {p.label}
          </Button>
        ))}
      </div>

      <p className="text-sm text-slate-500">{timeline.length} movimientos encontrados</p>

      {timeline.length === 0 ? (
        <Card className="p-12 text-center border-0 shadow-sm">
          <Clock className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Sin movimientos en este periodo</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {timeline.map((item, idx) => {
            const tc = typeConfig[item.type] || typeConfig.wallet;
            const Icon = tc.icon;
            const amt = getAmount(item);
            const st = getStatus(item);
            return (
              <div key={item.id ?? `${item.type}-${item.date}-${idx}`} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-100 hover:shadow-sm transition-shadow" data-testid={`history-${idx}`}>
                <div className={`w-9 h-9 rounded-xl ${tc.bg} flex items-center justify-center shrink-0`}>
                  <Icon className={`w-4 h-4 ${tc.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{getDescription(item)}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-slate-400">{new Date(item.date).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                    {st && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${st === 'completed' || st === 'approved' ? 'bg-green-100 text-green-700' : st === 'pending' || st === 'processing' ? 'bg-yellow-100 text-yellow-700' : st === 'cancelled' || st === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{statusLabel[st] || st}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {amt > 0 ? <ArrowUpCircle className="w-4 h-4 text-green-500" /> : <ArrowDownCircle className="w-4 h-4 text-red-400" />}
                  <span className={`text-sm font-semibold ${amt > 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {amt > 0 ? '+' : ''}${Math.abs(amt).toFixed(2)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientHistoryPage;
